import isArray from 'lodash-es/isArray'
import type { ReactNode } from 'react'
import React from 'react'

import type { AuthGroup, AuthRole } from '@fourthwall/auth'
import { useRoles } from '@fourthwall/auth'

import { groupsPermissions } from './permissions/groupsPermissions'
import type { DashboardPermissions, Permissions } from './types'

type Props = {
  children: ReactNode
}

export const DashboardPermissionsContext = React.createContext<DashboardPermissions | undefined>(
  undefined,
)

export const DashboardPermissionsProvider = ({ children }: Props) => {
  const { hasRole, hasGroup } = useRoles()

  const hasPermission = (permission: AuthGroup | AuthRole): boolean => {
    if (permission.startsWith('ROLE_')) {
      return hasRole(permission as AuthRole)
    }

    return hasGroup(permission as AuthGroup)
  }

  const buildGroupsPermissions = (
    permissions: Permissions<(AuthGroup | AuthRole)[]>,
  ): DashboardPermissions => {
    const result: Permissions<boolean> = {}

    Object.entries(permissions).forEach(([permission, value]) => {
      if (isArray(value)) {
        result[permission] = value.some(hasPermission)

        return
      }

      result[permission] = buildGroupsPermissions(value)
    })

    return result as DashboardPermissions
  }

  const getPermissions = () => {
    return buildGroupsPermissions(groupsPermissions)
  }

  return (
    <DashboardPermissionsContext.Provider value={getPermissions()}>
      {children}
    </DashboardPermissionsContext.Provider>
  )
}
